import { entity as E } from "./schema.js";
export const banking = {
"BB-BNK-01": [
E("bnk_customer", "Banking customer relationship", "A bank-issued customer relationship linked to a shared party.", `party_id|>pty_party.party_id|Person or organization served
customer_number|CODE(40)|Bank-issued customer reference
opened_on|DATE|Relationship start
status|CODE(20)|Prospect, active or closed
risk_review_ref|TEXT(120)?|Reference to a separately governed assessment`, ["customer_number"], "Customer number scope is one bank; this is not a customer due-diligence implementation."),
E("bnk_relationship_role", "Customer relationship role", "A dated party role in a banking customer relationship.", `customer_id|>bnk_customer.id|Customer relationship
party_id|>pty_party.party_id|Related party
role_code|CODE(40)|Beneficial owner, representative or contact
valid_from|DATE|Role start
valid_to|DATE?|Role end`, ["customer_id,party_id,role_code,valid_from"], "Role authority and verification evidence require review; party identity is not inferred from names."),
E("bnk_relationship_manager", "Relationship-manager assignment", "A dated employee assignment to service a banking customer.", `customer_id|>bnk_customer.id|Serviced customer
employee_id|>org_employee.employee_id|Assigned relationship manager
starts_on|DATE|Assignment start
ends_on|DATE?|Assignment end
service_role|CODE(30)|Primary or supporting manager`, ["customer_id,employee_id,starts_on"], "Concurrent primary managers require an explicit servicing policy.")
],
"BB-BNK-02": [
E("bnk_product", "Banking account product", "A versioned account offer with currency and servicing characteristics.", `product_code|CODE(40)|Product identifier
version|INTEGER|Terms version
product_kind|CODE(30)|Current, savings or settlement
currency|CODE(3)|Account currency
valid_from|DATE|Terms effective date`, ["product_code,version"], "Rate and fee terms belong to separately versioned agreements."),
E("bnk_account", "Bank account", "An administered deposit or settlement account, distinct from a ledger account.", `product_id|>bnk_product.id|Governing product version
account_number|CODE(40)|Bank-local account reference
branch_code|CODE(30)|Servicing branch reference
opened_on|DATE|Opening date
status|CODE(20)|Pending, active, blocked or closed`, ["account_number"], "Account balances derive from accepted entries; the template supplies no transaction engine."),
E("bnk_account_holder", "Account holder", "A customer's ownership or signatory role on an account.", `account_id|>bnk_account.id|Administered account
customer_id|>bnk_customer.id|Banking customer
role_code|CODE(30)|Owner, joint_owner or signatory
valid_from|DATE|Role start
valid_to|DATE?|Role end`, ["account_id,customer_id,role_code,valid_from"], "Joint ownership and signing mandates require explicit local rules."),
E("bnk_account_status", "Account status event", "History of account status decisions.", `account_id|>bnk_account.id|Affected account
event_no|INTEGER|Sequence per account
status|CODE(20)|Resulting state
effective_at|INSTANT|Effective timestamp
reason|TEXT(500)|Reason for the change`, ["account_id,event_no"], "Status transitions must be authorized and ordered by effective time.")
],
"BB-BNK-03": [
E("bnk_deposit", "Term deposit agreement", "A dated placement associated with a banking account.", `account_id|>bnk_account.id|Funding and servicing account
agreement_number|CODE(40)|Deposit agreement reference
principal|MONEY|Initial principal in account currency
starts_on|DATE|Value date
matures_on|DATE|Contract maturity`, ["agreement_number"], "Principal is not an available balance; day-count and compounding are explicit agreement terms."),
E("bnk_deposit_rate", "Deposit rate period", "An effective interest-rate interval for a deposit.", `deposit_id|>bnk_deposit.id|Term agreement
valid_from|DATE|Rate start
valid_to|DATE|Rate end
annual_rate|PCT|Annual rate percentage
day_count_basis|CODE(20)|Agreed day-count convention`, ["deposit_id,valid_from"], "Rate intervals must not overlap; no interest formula is implied."),
E("bnk_interest_accrual", "Deposit interest accrual", "A calculated accrual amount retained with its calculation period.", `deposit_id|>bnk_deposit.id|Term deposit
accrual_date|DATE|Accrual value date
amount|MONEY|Accrued amount in account currency
calculation_ref|TEXT(120)|Reviewed calculation version`, ["deposit_id,accrual_date"], "Corrections require auditable adjustment entries."),
E("bnk_maturity_instruction", "Deposit maturity instruction", "Versioned instructions for principal and interest at maturity.", `deposit_id|>bnk_deposit.id|Maturing deposit
revision|INTEGER|Instruction revision
action|CODE(30)|Renew, close or partial_renewal
destination_id|>bnk_account.id|Authorized destination account
recorded_at|INSTANT|Instruction time`, ["deposit_id,revision"], "Only the approved effective instruction can direct funds.")
],
"BB-BNK-04": [
E("bnk_loan", "Loan agreement", "A borrowing agreement with principal and repayment basis.", `customer_id|>bnk_customer.id|Primary borrower
loan_number|CODE(40)|Agreement reference
currency|CODE(3)|Loan currency
principal_limit|MONEY|Approved principal limit
starts_on|DATE|Agreement start
matures_on|DATE|Final maturity`, ["loan_number"], "This is a structural lending example; underwriting and legal terms are external."),
E("bnk_disbursement", "Loan disbursement", "One release of principal under a loan agreement.", `loan_id|>bnk_loan.id|Loan agreement
sequence_no|INTEGER|Disbursement sequence
account_id|>bnk_account.id|Receiving account
value_date|DATE|Disbursement date
amount|MONEY|Principal in loan currency`, ["loan_id,sequence_no"], "Aggregate disbursement must respect the approved limit."),
E("bnk_installment", "Repayment installment", "One scheduled principal and interest obligation.", `loan_id|>bnk_loan.id|Loan agreement
schedule_revision|INTEGER|Repayment schedule version
installment_no|INTEGER|Installment sequence
due_on|DATE|Due date
principal_due|MONEY|Principal amount
interest_due|MONEY|Interest amount`, ["loan_id,schedule_revision,installment_no"], "Rescheduling preserves prior revisions and their payment allocations."),
E("bnk_repayment", "Loan repayment allocation", "A received amount allocated to an installment.", `installment_id|>bnk_installment.id|Scheduled obligation
receipt_reference|CODE(60)|Payment receipt reference
value_date|DATE|Effective repayment date
principal_paid|MONEY|Principal applied
interest_paid|MONEY|Interest applied`, ["installment_id,receipt_reference"], "Overpayments, reversals and fees need explicit policies; no implicit netting.")
],
"BB-BNK-05": [
E("bnk_payment", "Payment instruction", "A payment request with source account and explicit beneficiary reference.", `instruction_reference|CODE(60)|Idempotent payment instruction reference
source_account_id|>bnk_account.id|Debited account
beneficiary_reference|TEXT(120)|External or internal beneficiary identifier
amount|MONEY|Requested payment amount
currency|CODE(3)|Payment currency
requested_on|DATE|Requested value date`, ["instruction_reference"], "Beneficiary validation, exchange rates and authorization are outside this template."),
E("bnk_payment_event", "Payment execution event", "Ordered processing events for a payment instruction.", `payment_id|>bnk_payment.id|Payment instruction
event_no|INTEGER|Processing sequence
occurred_at|INSTANT|Event time
status|CODE(30)|Accepted, rejected, sent, settled or reversed
network_reference|TEXT(120)?|External settlement reference`, ["payment_id,event_no"], "A request is not settled until confirmed by the processing system."),
E("bnk_payment_fee", "Payment charge", "An assessed fee recorded separately from payment principal.", `payment_id|>bnk_payment.id|Payment instruction
fee_no|INTEGER|Fee component
fee_code|CODE(40)|Fee classification
amount|MONEY|Fee amount
currency|CODE(3)|Fee currency`, ["payment_id,fee_no"], "Fee currency may differ from payment currency; conversions require evidence.")
],
"BB-BNK-06": [
E("bnk_card", "Card instrument", "A tokenized payment instrument linked to an account and customer.", `account_id|>bnk_account.id|Settlement account
customer_id|>bnk_customer.id|Cardholder
instrument_token|CODE(100)|Non-secret token from card platform
expires_on|DATE|Expiry date
status|CODE(20)|Issued, active, blocked or expired`, ["instrument_token"], "Never store full card numbers, PINs or verification codes in this example."),
E("bnk_card_authorization", "Card authorization", "A merchant authorization request and its decision.", `card_id|>bnk_card.id|Tokenized instrument
authorization_reference|CODE(60)|Processor authorization reference
requested_at|INSTANT|Request timestamp
amount|MONEY|Authorized amount
currency|CODE(3)|Transaction currency
decision|CODE(20)|Approved, declined or reversed`, ["authorization_reference"], "Authorizations are holds, not posted account transactions."),
E("bnk_card_transaction", "Posted card transaction", "A clearing record linked where available to its authorization.", `card_id|>bnk_card.id|Instrument
authorization_id|>bnk_card_authorization.id?|Prior authorization
clearing_reference|CODE(60)|Clearing identifier
posted_on|DATE|Posting date
amount|MONEY|Cleared amount
currency|CODE(3)|Cleared currency`, ["clearing_reference"], "Offline and adjusted clearing may not have a matching authorization."),
E("bnk_card_dispute", "Card dispute", "A dispute about a posted card transaction.", `transaction_id|>bnk_card_transaction.id|Disputed transaction
case_number|CODE(40)|Dispute reference
opened_on|DATE|Dispute date
reason|CODE(40)|Dispute reason
status|CODE(20)|Open, investigating or resolved`, ["case_number"], "Dispute time limits and chargeback rules are governed by the relevant network.")
],
"BB-BNK-07": [
E("bnk_instrument", "Treasury instrument", "A treasury instrument identified within a declared issuer namespace.", `issuer_id|>pty_party.party_id|Instrument issuer
instrument_code|CODE(60)|Issuer-scoped identifier
instrument_kind|CODE(30)|Bond, money_market or FX
currency|CODE(3)|Nominal currency`, ["issuer_id,instrument_code"], "A code is not assumed to be an ISIN unless explicitly validated."),
E("bnk_deal", "Treasury deal", "An agreed trade with a counterparty.", `instrument_id|>bnk_instrument.id|Traded instrument
counterparty_id|>pty_party.party_id|Trading counterparty
deal_reference|CODE(60)|Trading-book deal reference
trade_date|DATE|Agreement date
notional|MONEY|Agreed notional
side|CODE(10)|Buy or sell`, ["deal_reference"], "Pricing, valuation and risk calculations are not provided."),
E("bnk_cashflow", "Treasury cash flow", "A contractual settlement obligation for a treasury deal.", `deal_id|>bnk_deal.id|Originating deal
flow_no|INTEGER|Flow sequence
due_on|DATE|Settlement date
amount|MONEY|Amount payable or receivable
currency|CODE(3)|Settlement currency
direction|CODE(10)|Pay or receive`, ["deal_id,flow_no"], "Netting requires an explicit enforceable agreement and separate calculation."),
E("bnk_position_snapshot", "Treasury position snapshot", "A dated derived position retained with calculation provenance.", `instrument_id|>bnk_instrument.id|Instrument
book_code|CODE(40)|Trading book
as_of|INSTANT|Position timestamp
quantity|QTY|Derived position quantity
calculation_ref|TEXT(120)|Valuation process reference`, ["instrument_id,book_code,as_of"], "Snapshots do not replace underlying deal and settlement history.")
],
"BB-BNK-08": [
E("bnk_collateral", "Pledged collateral", "An asset offered by a party as security.", `owner_id|>pty_party.party_id|Declared legal owner
collateral_reference|CODE(60)|Collateral register reference
asset_kind|CODE(30)|Property, security, cash or guarantee
description|TEXT(500)|Asset description`, ["collateral_reference"], "Ownership and enforceability are externally verified facts."),
E("bnk_collateral_valuation", "Collateral valuation", "A dated valuation with its currency and basis.", `collateral_id|>bnk_collateral.id|Valued asset
valued_on|DATE|Valuation date
valuation_reference|CODE(60)|Independent valuation reference
amount|MONEY|Assessed value
currency|CODE(3)|Valuation currency`, ["collateral_id,valuation_reference"], "Valuation does not establish recoverable value or legal priority."),
E("bnk_security_agreement", "Security agreement", "An agreement associating collateral with a borrowing exposure.", `collateral_id|>bnk_collateral.id|Pledged asset
loan_id|>bnk_loan.id|Secured loan
agreement_reference|CODE(60)|Security instrument reference
priority_rank|INTEGER|Declared security ranking
secured_amount|MONEY|Allocated amount in loan currency`, ["agreement_reference,collateral_id,loan_id"], "Cross-collateralization and priority conflicts require legal and credit review.")
],
"BB-BNK-09": [
E("bnk_reconciliation", "Banking reconciliation run", "A controlled comparison of external and internal entries.", `account_id|>bnk_account.id|Reconciled account
run_reference|CODE(40)|Reconciliation reference
as_of|DATE|Cutoff date
status|CODE(20)|Open, reviewed or closed`, ["run_reference"], "Closing requires all differences to be resolved or explicitly carried forward."),
E("bnk_reconciliation_item", "Reconciliation item", "A captured external entry with a candidate internal reference.", `run_id|>bnk_reconciliation.id|Reconciliation run
line_no|INTEGER|Source line sequence
external_reference|TEXT(120)|Statement source reference
internal_reference|TEXT(120)?|Candidate internal transaction reference
amount|MONEY|Amount in account currency
match_status|CODE(20)|Unmatched, partial or matched`, ["run_id,line_no"], "Matching by amount alone cannot establish transaction identity."),
E("bnk_reconciliation_exception", "Reconciliation exception", "A reviewed difference and its resolution ownership.", `item_id|>bnk_reconciliation_item.id|Unresolved item
exception_no|INTEGER|Exception sequence
owner_id|>org_employee.employee_id|Assigned employee
reason|TEXT(500)|Difference explanation
resolution|TEXT(1000)?|Reviewed resolution
resolved_at|INSTANT?|Resolution time`, ["item_id,exception_no"], "Resolution evidence is required before an item becomes matched.")
],
"BB-BNK-10": [
E("bnk_cash_point", "Vault or teller cash point", "A separately controlled physical cash holding location.", `location_id|>org_location.location_id|Branch location
cash_point_code|CODE(40)|Vault or till reference
cash_point_kind|CODE(20)|Vault, teller or ATM
status|CODE(20)|Open, suspended or closed`, ["location_id,cash_point_code"], "Cash custody and dual-control requirements belong to operations."),
E("bnk_cash_transfer", "Cash transfer", "A cash movement between two controlled locations.", `transfer_number|CODE(40)|Cash movement reference
from_point_id|>bnk_cash_point.id|Dispatch location
to_point_id|>bnk_cash_point.id|Receiving location
transferred_at|INSTANT|Transfer time
currency|CODE(3)|Cash currency`, ["transfer_number"], "Source and destination must differ; dispatch and receipt require reconciliation."),
E("bnk_denomination_line", "Transfer denomination", "The count and face value of a cash denomination being moved.", `transfer_id|>bnk_cash_transfer.id|Cash transfer
face_value|MONEY|Value of one note or coin
cash_kind|CODE(10)|Note or coin
unit_count|INTEGER|Count transferred`, ["transfer_id,face_value,cash_kind"], "Counts must be nonnegative integers; value is derived from denomination times count."),
E("bnk_cash_count", "Cash count result", "A physical denomination count at a cash point.", `point_id|>bnk_cash_point.id|Count location
session_reference|CODE(40)|Count session
currency|CODE(3)|Counted currency
face_value|MONEY|Denomination value
cash_kind|CODE(10)|Note or coin
unit_count|INTEGER|Observed count
counted_at|INSTANT|Count time`, ["point_id,session_reference,currency,face_value,cash_kind"], "Any adjustment requires an approved movement; do not overwrite prior counts.")
],
"BB-BNK-11": [
E("bnk_equipment_custody", "Branch equipment custody", "Dated custody of a tracked asset by a branch employee.", `asset_id|>ast_asset.asset_id|Equipment asset
employee_id|>org_employee.employee_id|Custodian
issued_on|DATE|Custody start
returned_on|DATE?|Custody end
condition_code|CODE(30)|Condition at handover`, ["asset_id,issued_on"], "An asset cannot have incompatible active custody assignments."),
E("bnk_supply_request", "Branch consumable request", "A branch request for replenishment of a shared inventory item.", `location_id|>org_location.location_id|Requesting branch
request_number|CODE(40)|Branch request reference
item_id|>inv_item.item_id|Requested consumable
quantity|QTY|Requested quantity
uom|CODE(16)|Quantity unit
required_on|DATE|Required date`, ["location_id,request_number"], "Request approval and stock issue are separate events."),
E("bnk_equipment_disposal", "Equipment disposal decision", "Approval record for retirement or transfer of bank equipment.", `asset_id|>ast_asset.asset_id|Asset being disposed
decision_reference|CODE(40)|Disposal decision reference
decided_on|DATE|Decision date
method|CODE(30)|Sale, transfer, recycle or destruction
evidence_ref|TEXT(200)|Custody and sanitization evidence`, ["decision_reference"], "Data sanitization and financial write-off must be independently verified.")
],
"BB-BNK-12": [
E("bnk_posting_rule", "Banking posting rule", "A versioned mapping from banking events to shared ledger accounts.", `event_code|CODE(40)|Banking event classification
version|INTEGER|Rule version
debit_account_id|>fin_account.account_id|Debit ledger account
credit_account_id|>fin_account.account_id|Credit ledger account
valid_from|DATE|Effective date`, ["event_code,version"], "Rule approval and multi-leg accounting are controlled separately."),
E("bnk_posting_batch", "Banking posting batch", "A reconciled group of operational events submitted to a ledger.", `batch_reference|CODE(40)|Posting batch identifier
ledger_id|>fin_ledger.ledger_id|Destination ledger
business_date|DATE|Accounting business date
status|CODE(20)|Prepared, validated or posted
control_total|MONEY|Expected amount in ledger currency`, ["batch_reference"], "Control totals do not replace balanced double-entry validation."),
E("bnk_posting_source", "Banking journal provenance", "Link between a banking event and its accepted journal.", `batch_id|>bnk_posting_batch.id|Posting batch
source_system|CODE(60)|Issuing operational system namespace
source_reference|CODE(100)|Immutable operational event reference within its source system
rule_id|>bnk_posting_rule.id|Applied rule version
journal_id|>fin_journal.journal_id|Accepted ledger journal
amount|MONEY|Source amount in ledger currency`, ["source_system,source_reference"], "Each source event posts once across all batches; reversals are separate source events referencing the original.")
]
};
